import React from "react";
import { BLOCKS, MARKS,INLINES } from "@contentful/rich-text-types";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import slugify from "slugify";
import EmbedCode from "./embedCode";
import { color } from "framer-motion";

const useResolveBlogText = (rawHTML) => {
	const references = rawHTML.references || [];

    const findAsset = (id) => {
        return references.find(ref => ref.contentful_id === id);
    };

	const options = {
		renderMark: {
			[MARKS.CODE]: (text) => (
			  <pre className="code-block">
				<code>{text}</code>
			  </pre>
			),
		},
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, children) => {
				const htmlContent = children.join("");
				if (htmlContent.includes("<iframe") || htmlContent.includes("twitter-tweet")) {
					return <EmbedCode htmlContent={htmlContent} />;

				}
				return (
					<p className="text-p1 text-fw-regular text-clr-primary gap-2x">
						{children}
					</p>
				);
			},
			[BLOCKS.HEADING_1]: (node, children) => {
				return <h1>{children}</h1>;
			},
			[BLOCKS.HEADING_2]: (node, children) => {
				return (
					<h2 className="text-h2 gap-4x topic-title text-fw-medium">
						{children}
					</h2>
				);
			},
			[BLOCKS.HEADING_3]: (node, children) => {
				return (
					<h3 className="text-h3 gap-2x text-clr-primary text-fw-medium">
						{children}
					</h3>
				);
			},
			[BLOCKS.HEADING_4]: (node, children) => {
				return <h4 className="text-h4 gap-1x text-clr-primary text-fw-medium">{children}</h4>;
			},
			[BLOCKS.HEADING_5]: (node, children) => {
				return <h5 className=" text-clr-primary text-fw-medium">{children}</h5>;
			},
			[BLOCKS.HEADING_6]: (node, children) => {
				return <h6 className=" text-clr-primary text-fw-medium">{children}</h6>;
			},
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				const { gatsbyImageData,title } = node.data.target;
				const isValidUrl = (url) => {
					const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(\/.*)?$/;
					return urlRegex.test(url);
				};		
			

				if (node.data.target.file.contentType === "image/png") {
					if (gatsbyImageData) {
						const imageElement = (
							<GatsbyImage image={getImage(gatsbyImageData)} className="gap-3x" alt={title || "Image"} />
						);
						
						if (isValidUrl(title)) {
							return (
								<a href={title} target="_blank" rel="noopener noreferrer">
									{imageElement}
								</a>
							);
						}
				
						return imageElement;
					}
					return null;
				}
			},
			[BLOCKS.TABLE]: (node, children) => (
				<div className="rich-text-table">
					<table>
						<tbody>{children}</tbody>
					</table>
				</div>
			),
			[BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
			[BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,
			[BLOCKS.CODE]: (node) => {
				return (
					<pre className="code-block">
					<code>{node.content[0].value}</code>
					</pre>
				);
			},
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				const { gatsbyImageData, title } = node.data.target;
                
				const isValidUrl = (url) => {
					const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(\/.*)?$/;
					return urlRegex.test(url);
				};				

				
				if (node.data.target) {
					// if (file.contentType === "video/mp4")
					// 	return (
					// 		<InViewLogoAnimationController animationName={"play"}>
					// 			<video controls>
					// 				<source src={file.url} type={file.contentType} />
					// 			</video>
					// 		</InViewLogoAnimationController>
					// 	);

					// if (file.contentType === "application/json")
					// 	return (
					// 		<div className="lottie-player-wrap">
					// 			<Lottie
					// 				className="lottie-player"
					// 				options={{
					// 					loop: true,
					// 					autoplay: true,
					// 					path: file.url,
					// 					rendererSettings: {
					// 						preserveAspectRatio: "xMidYMid slice",
					// 					},
					// 				}}
					// 			/>
					// 		</div>
					// 	);

					if (gatsbyImageData) {

						
						const imageElement = (
							<GatsbyImage image={getImage(gatsbyImageData)} className="gap-3x" alt={title || "Image"} />
						);
				
						if (isValidUrl(title)) {
							return (
								<a href={title} target="_blank" rel="noopener noreferrer">
									{imageElement}
								</a>
							);
						}
						return imageElement;
					}
                     return null;				
				}
			},
			[BLOCKS.QUOTE]: (node, children) => {
				const textContent = React.Children.toArray(children)
				.map((child) => (typeof child === "string" ? child : child.props?.children || ""))
				.join("");
				const paragraphs = textContent.split("\n").filter((line) => line.trim() !== ""); // Remove empty lines
			
				return (
					<blockquote className="blog-blockquote">
						{paragraphs.map((paragraph, index) => (
							<p
								key={index}
								className={`text-p1 font--primary text-fw-medium text-clr-white blog-paragraph ${
									index === 0 ? "first-paragraph" : index === paragraphs.length - 1 ? "last-paragraph" : "middle-paragraph"
								}`}
							>
								{paragraph.trim()}
							</p>
						))}
					</blockquote>
				);
			},
			[INLINES.ASSET_HYPERLINK]: (node) => {
                const assetId = node.data.target.sys.id;
                const asset = findAsset(assetId);

                if (asset) {
                    const imageData = getImage(asset.gatsbyImageData);
                    const url = node.content[0].value;

                    return (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                            <GatsbyImage image={imageData} alt={asset.title} />
                        </a>
                    );
                }
                return null;
            },
			[INLINES.HYPERLINK]: (node) => {
				const { uri } = node.data;
				
				const children = node.content.map((child, index) => {
					if (child.nodeType === 'text') {
						return child.marks.length > 0 && child.marks[0].type === 'underline' ? (
							<span key={index} className="text-link">
								{child.value}
							</span>
						) : (
							<span key={index}>{child.value}</span>
						);
					}
					return null;
				});
				return (
					<a href={uri} target="_blank" rel="noopener noreferrer" className="text-clr-black">
						{children}
					</a>
				);
			},
		},
	};
	return buildLayout(renderRichText(rawHTML, options));
};

function buildLayout(elements, i) {
	let leftContent = elements
		.filter((el, i) => el?.type === "h2")
		.map((e) => {
			return (
				<a
					href={"#" + slugify(`${e.props.children}`, { remove: /[%]/g })}
					className="chapter text-p1 text-fw-regular text-clr-primary"
				>
					{e.props.children[0]}
				</a>
			);
		});
	let rightContent = elements.map((el, i) => {
		if (el?.type === "h2") {
			return React.createElement(
				"h2",

				{
					id: slugify(`${el.props.children}`, { remove: /[%]/g }),
					className: "text-h2 right-el gap-4x text-clr-primary text-fw-medium",
				},
				el.props.children
			);
		}
		return el;
	});

	const header = React.createElement(
		"div",
		{
			className:
				"blog-row__left-header gap-3x text-h3 text-clr-primary text-fw-medium",
		},
		"Chapters"
	);
	const titlewrap = React.createElement(
		"div",
		{
			className: "title-wrap",
		},

		leftContent
	);
	const leftColWrap = React.createElement(
		"div",
		{
			className: "wrapper",
		},
		[header, titlewrap]
	);

	const innerlft = React.createElement(
		"div",
		{ className: "blog-detail-content__row-left" },

		leftColWrap
	);
	const content = React.createElement(
		"div",
		{ className: "blog-detail-content__row-right" },
		rightContent
	);
	const wrapper = React.createElement(
		"div",
		{ className: "blog-detail-content__row", key: i },
		innerlft,
		content
	);

	return wrapper;
}
export default useResolveBlogText;


